import { formatMinutes } from "../relativeTime";

/**
 * How long a new guild takes to fill at a given join rate.
 *
 * `bots.population.join_probability` is a chance per EMPTY SEAT per population
 * sweep, and the population worker sweeps every five minutes. "3 %" is not a
 * number anybody converts to "about nine hours" in their head, so the field
 * draws the curve — and it has to be the curve the worker really walks, not a
 * gradient that looks like one.
 *
 * The model, at probability `p` and a target of `T` seats:
 *
 *   * every free seat is drawn independently on every sweep, so the share of
 *     seats taken after `n` sweeps is `1 − (1 − p)^n`. Solving that for a share
 *     gives the first two ticks: half the seats at `ln(0.5)/ln(1 − p)` sweeps,
 *     four fifths at `ln(0.2)/ln(1 − p)`;
 *   * filling ALL of them is the expected maximum of `T` geometric draws,
 *     `H_T / p` sweeps, where `H_T = 1 + 1/2 + … + 1/T`. That is the last tick
 *     and the sentence above the bar.
 *
 * Which is why the bar is fast at the start and slow at the end: the last seat
 * of sixteen waits about as long as the first eight together.
 *
 * The two ends are not this arithmetic. At `p = 1` every empty seat is taken on
 * the first sweep — `ln(1 − p)` is `-Infinity` and `H_T / p` would claim three
 * sweeps, both wrong — and at `p = 0` no seat is ever taken, which is a
 * sentence rather than a division by zero.
 */

/** How often `PopulationWorker` sweeps. */
export const SWEEP_MINUTES = 5;

/** One tick of the bar: when it happens, where it is drawn, what it says. */
export interface FillPoint {
  /** Sweeps until this share of the seats is taken. */
  sweeps: number;
  /** Position on the bar, 0–100, linear in time against the last tick. */
  percent: number;
  /** Tick label — "1 h", the same shape the rest of the Bots page uses. */
  short: string;
  /** Spelled out for the sentence above the bar — "9 hours". */
  long: string;
}

export type JoinRateFill =
  /** `p = 0` — nothing is ever seated. */
  | { kind: "never" }
  /** `p = 100 %` — every empty seat is taken on the first sweep. */
  | { kind: "one_sweep" }
  | {
      kind: "curve";
      half: FillPoint;
      fourFifths: FillPoint;
      all: FillPoint;
    };

/** `H_n = 1 + 1/2 + … + 1/n`. */
function harmonic(n: number): number {
  let sum = 0;
  for (let i = 1; i <= n; i += 1) sum += 1 / i;
  return sum;
}

/** "9 hours" / "12 minutes" / "2 days" — the long half of a tick's label. */
function spell(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function point(sweeps: number, ofTotal: number): FillPoint {
  const minutes = Math.round(sweeps * SWEEP_MINUTES);
  return {
    sweeps,
    // Clamped, because the first two ticks come from the share curve and the
    // last from the expected maximum: on a guild of one or two seats "four
    // fifths of the seats" can land past "all of them", and a tick outside the
    // bar is worse than one sitting on its end.
    percent: ofTotal > 0 ? Math.min(100, (sweeps / ofTotal) * 100) : 100,
    short: formatMinutes(minutes),
    long: spell(minutes),
  };
}

/**
 * The same tick at the two ends of a target range, as the phrase that names
 * both and as the tail that follows the first one.
 *
 * Two forms because the preview says the span twice, in sentences of different
 * shapes: "fill in about 6 to 7 hours" and "between 6 hours in the smallest
 * guild and 7 in the largest". The unit is dropped from the shorter half only
 * when both ends are spelled in the same one — "50 minutes to 2 hours" keeps
 * both.
 */
export function spellSpan(
  from: FillPoint,
  to: FillPoint,
): { joined: string; tail: string } {
  if (from.long === to.long) return { joined: to.long, tail: to.long };
  const unit = (long: string) => long.split(" ")[1].replace(/s$/, "");
  if (unit(from.long) !== unit(to.long)) {
    return { joined: `${from.long} to ${to.long}`, tail: to.long };
  }
  return {
    joined: `${from.long.split(" ")[0]} to ${to.long}`,
    tail: to.long.split(" ")[0],
  };
}

/**
 * joinRateFill answers "how long does a new guild of `seats` seats take to
 * fill at `percent` % per seat per sweep".
 */
export function joinRateFill(percent: number, seats: number): JoinRateFill {
  const p = percent / 100;
  if (!Number.isFinite(p) || p <= 0) return { kind: "never" };
  if (p >= 1) return { kind: "one_sweep" };

  // A target the config has not been given yet still has to draw something —
  // and a guild always holds at least the one seat being filled.
  const target = Math.max(1, Math.floor(seats) || 1);
  const all = harmonic(target) / p;
  return {
    kind: "curve",
    half: point(Math.log(0.5) / Math.log(1 - p), all),
    fourFifths: point(Math.log(0.2) / Math.log(1 - p), all),
    all: point(all, all),
  };
}
